import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { useFetcher } from 'react-router'
import { twMerge } from 'tailwind-merge'
import type { loader as insuranceSearchLoader } from '../routes/resource.insurance-search'

/** A plan the visitor confirmed by picking it out of the suggestion list. */
export type SelectedInsurance = {
  /** Ribbon uuid — what `?insurance=` carries and the form submits. */
  id: string
  /** Non-null in the contract; the only string shown as the field value. */
  displayName: string
  /** Never blank — the resource loader drops rows without one. */
  carrierName: string
  /** 2-letter USPS, or null for a national row. */
  state: string | null
}

type InsuranceComboboxProps = {
  /** Form-control name — the hidden input carries the selected plan id under this name. */
  name: string
  label?: string | undefined
  /** Merged over the root wrapper's base classes (via twMerge), so a host can
   *  make the control participate in its own layout — the search row uses it to
   *  distribute the label and field over the full cell height. */
  className?: string | undefined
  /** Classes for the `<label>`. Pass `sr-only` where the design has no visible
   *  label text — the control keeps a real label either way. */
  labelClassName?: string | undefined
  /** Merged over the input's base classes (via twMerge), so a host can restyle
   *  the field without forking the component. */
  inputClassName?: string | undefined
  placeholder?: string | undefined
  /** Currently-selected plan. Pass `null` to clear. */
  value: SelectedInsurance | null
  onChange: (next: SelectedInsurance | null) => void
  /** The 2-letter USPS state being searched, for catalog scope and relevance.
   *  Passed in
   *  rather than read from `useGeo()` here: the host already calls it, and a
   *  geo fetcher inside the combobox would make its unit test a router dance. */
  state?: string | undefined
  'data-testid'?: string | undefined
}

const DEBOUNCE_MS = 300
// Match the resource loader's guard so the dropdown doesn't flash for 0–1 chars.
const MIN_QUERY_LENGTH = 2

/**
 * Insurance-plan type-ahead for the Atlas Web provider search. The visitor types
 * a carrier ("aetna"), picks their plan from the real Ribbon catalog, and the
 * form submits the plan's uuid under the hood via a hidden input.
 *
 * Cloned from `ZipCombobox`, which was itself cloned from Console's
 * `SpecialtyCombobox` — the repo keeps these independent by design (see the
 * VIT-17093 note on zip-combobox.tsx). This is the third instance, which is
 * where extracting a shared base starts paying for itself; deliberately not done
 * here so this ticket stays reviewable against a known-good template.
 *
 * Unlike ZIP, a plan id is opaque: the visible text is the catalog's
 * `displayName` and can only come from a picked row (or from the results loader
 * resolving `?insurance=`), never from parsing the value.
 */
export const InsuranceCombobox = ({
  name,
  label,
  className = undefined,
  labelClassName = undefined,
  inputClassName = undefined,
  placeholder = 'e.g. Aetna',
  value,
  onChange,
  state = undefined,
  'data-testid': dataTestId,
}: InsuranceComboboxProps) => {
  const [query, setQuery] = useState(value?.displayName ?? '')
  const [open, setOpen] = useState(false)
  // Index of the keyboard-highlighted suggestion; -1 means none highlighted, so
  // the first ArrowDown lands on item 0.
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const inputId = useId()
  // Stable ids for the ARIA combobox↔listbox↔option wiring.
  const listboxId = useId()
  const optionId = (index: number) => `${listboxId}-option-${index}`
  // useId() emits colons, which aren't valid in CSS anchor-name / position-anchor
  // identifiers — strip them so the anchor positioning binds. Each instance gets
  // its own anchor so multiple comboboxes don't collide.
  const anchorName = `--insurance-anchor-${useId().replace(/:/g, '')}`
  // Distinguish "we cleared the value as a side effect of typing" (no resync)
  // from "parent externally changed the value" (resync).
  const skipNextValueSyncRef = useRef(false)

  const fetcher = useFetcher<typeof insuranceSearchLoader>()
  const loading = fetcher.state !== 'idle'

  // `useFetcher` keeps the previous response until the next debounced load
  // resolves, so right after the query changes it still holds the old input's
  // rows. This is a fuzzy ranked search — no prefix invariant to re-filter on the
  // way the ZIP combobox does — so the loader echoes the `input` it served and we
  // compare. Without it, a quick ArrowDown + Enter during the debounce window
  // could commit a plan that isn't on screen.
  const suggestions = fetcher.data?.input === query.trim() ? fetcher.data.items : []

  // The visible text for the committed selection, or '' when none. Used both as
  // the field value and as the "is the query still the committed value?"
  // comparison so refocus/hydration stay consistent.
  const selectedLabel = value?.displayName ?? ''

  // Sync the visible text whenever the parent flips the selected value
  // (e.g. clearing from outside, the results loader resolving `?insurance=`).
  useEffect(() => {
    if (skipNextValueSyncRef.current) {
      skipNextValueSyncRef.current = false
      return
    }
    setQuery(selectedLabel)
  }, [selectedLabel])

  // Debounce the lookup as the user types. Skip the fetch right after a
  // selection (query already equals the selected label) and for trivial queries.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetcher identity is stable; depending on it would re-run the debounce on every fetcher state transition.
  useEffect(() => {
    if (value != null && query === selectedLabel) return
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) return

    const handle = setTimeout(() => {
      const params = new URLSearchParams({ input: trimmed })
      if (state != null && state !== '') params.set('state', state)
      void fetcher.load(`/resource/insurance-search?${params.toString()}`)
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(handle)
    }
  }, [query, value, selectedLabel, state])

  // Close the suggestion list on outside click.
  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
    }
  }, [])

  // Whether the visible text still matches a committed selection. Load-bearing
  // here: a rehydrated pill holds a full plan name, and that is the worst
  // possible input for the carrier-first ranker (a long phrase dilutes the
  // carrier below the similarity floor). Without this, merely focusing the field
  // on a filtered results page would fire that search and pop a dropdown of
  // someone else's plans. As soon as the user edits, `value` is nulled and a
  // fresh fetch runs.
  const matchesCommittedSelection = value != null && query === selectedLabel

  // Only surface the dropdown for non-trivial queries so a stale fetcher result
  // doesn't reappear once the visitor deletes back below the minimum.
  const showDropdown =
    open && !matchesCommittedSelection && query.trim().length >= MIN_QUERY_LENGTH && (loading || suggestions.length > 0)

  // The suggestion list is a native popover (top layer + CSS anchor positioning),
  // so it escapes the form's stacking/overflow context and floats over the page.
  // A text input can't be a `popovertarget` invoker (only buttons can), so drive
  // show/hide imperatively from our own open state. Guard on :popover-open since
  // showPopover()/hidePopover() throw if called in the wrong state.
  useEffect(() => {
    const el = popoverRef.current
    if (!el) return
    try {
      if (showDropdown && !el.matches(':popover-open')) {
        el.showPopover()
      } else if (!showDropdown && el.matches(':popover-open')) {
        el.hidePopover()
      }
    } catch {
      // Popover API unavailable / element not connected — degrade to no dropdown.
    }
  }, [showDropdown])

  // Drop the highlight whenever the dropdown is hidden so it doesn't reappear
  // stale on the next open.
  useEffect(() => {
    if (!showDropdown) setActiveIndex(-1)
  }, [showDropdown])

  // An empty field is a valid unfiltered search, so this control is never
  // `required`. But typed free text with no picked row would submit an empty
  // hidden id and apply no filter at all, while the visitor believes one is
  // applied — so flag the input invalid in that state. Native constraint
  // validation runs before the `submit` event, so this gates the form's
  // hand-rolled onSubmit for free.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    const typedWithoutSelection = query.trim() !== '' && value == null
    el.setCustomValidity(
      typedWithoutSelection ? 'Please choose an insurance plan from the list, or clear the field.' : '',
    )
  }, [query, value])

  // Keep the highlighted row scrolled into view as the user arrows through a list
  // taller than the popover.
  useEffect(() => {
    if (activeIndex < 0) return
    const items = popoverRef.current?.querySelectorAll('[role="option"]')
    items?.[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleSelect = (suggestion: SelectedInsurance) => {
    onChange(suggestion)
    setQuery(suggestion.displayName)
    setOpen(false)
    // Committing a selection unmounts the option buttons (the dropdown is
    // suppressed once the query matches the selection), so a mouse-picked row
    // would otherwise drop focus to <body> and strand a keyboard user at the top
    // of the page.
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        // Open on first ArrowDown if there are results but the list is closed.
        if (!showDropdown) {
          setOpen(true)
          return
        }
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1))
        break
      case 'ArrowUp':
        if (!showDropdown) return
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        break
      case 'Enter': {
        // Select the highlighted row instead of submitting the form. With nothing
        // highlighted, let Enter fall through to the native form submit.
        const active = activeIndex >= 0 ? suggestions[activeIndex] : undefined
        if (active) {
          event.preventDefault()
          handleSelect(active)
        }
        break
      }
      case 'Escape':
        if (showDropdown) {
          event.preventDefault()
          setOpen(false)
          setActiveIndex(-1)
        }
        break
    }
  }

  return (
    <div ref={containerRef} className={twMerge('relative flex flex-col w-full', className)} data-testid={dataTestId}>
      {label != null && (
        <label htmlFor={inputId} className={labelClassName}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          // Browsers eagerly autofill anything named "insurance" from a saved
          // profile, which would put unpickable free text in the field.
          autoComplete="off"
          // ARIA combobox wiring so screen readers announce the suggestions, the
          // expanded state, and which option is highlighted.
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          // twMerge so a host's `inputClassName` replaces conflicting base
          // utilities rather than fighting them on specificity.
          className={twMerge('w-full bg-white border py-2 px-3 rounded', inputClassName)}
          // anchor-name binds this input as the positioning anchor for the popover.
          // Set via style (not a Tailwind class) since the anchor id is computed at
          // runtime and Tailwind can only emit classes it sees as literals at build.
          style={{ anchorName }}
          placeholder={placeholder}
          value={query}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            // Typing invalidates the current highlight — start fresh.
            setActiveIndex(-1)
            // Typing edits the in-flight selection — reflect that to the parent
            // immediately so it doesn't keep stale data. Mark the next value-sync
            // as a side effect of our own onChange so the effect doesn't wipe what
            // the user just typed.
            if (value != null && e.target.value !== selectedLabel) {
              skipNextValueSyncRef.current = true
              onChange(null)
            }
          }}
          onFocus={() => {
            setOpen(true)
          }}
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        />
        {/* Hidden field carries the selected plan id for the GET form submit. */}
        <input type="hidden" name={name} value={value?.id ?? ''} />
      </div>
      {/* Native popover (manual) as the ARIA listbox: rendered in the top layer
          and positioned by CSS anchor positioning against the input, so it floats
          above the page instead of being clipped by the form. A div (not ul/li)
          carries the listbox/option roles; focus stays in the input and the active
          option is tracked via aria-activedescendant. `inset-auto` + explicit
          margin override the UA-centered default. */}
      <div
        ref={popoverRef}
        id={listboxId}
        role="listbox"
        popover="manual"
        className="inset-auto [margin:0.25rem_0_0_0] [position-area:bottom_span-right] [position-try-fallbacks:flip-block] w-[anchor-size(width)] max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        // position-anchor points at the input's anchor-name; set via style for the
        // same runtime-id reason as the input's anchor-name above.
        style={{ positionAnchor: anchorName }}
        data-testid={dataTestId ? `${dataTestId}-suggestions` : undefined}
      >
        {loading && suggestions.length === 0 && <div className="px-3 py-2 text-slate-500">Searching…</div>}
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.id}
            type="button"
            id={optionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            // Out of the tab order: under the aria-activedescendant pattern
            // focus stays on the input and the arrow keys move the highlight, so
            // Tab must leave the combobox rather than walk the suggestions.
            tabIndex={-1}
            onClick={() => {
              handleSelect(suggestion)
            }}
            // Hovering syncs the keyboard highlight so mouse and keyboard don't
            // fight over which row is active.
            onMouseEnter={() => {
              setActiveIndex(index)
            }}
            className={`block w-full text-left px-3 py-2 transition-colors cursor-pointer ${
              index === activeIndex ? 'bg-primary-500 text-white' : 'hover:bg-secondary-100'
            }`}
            data-testid={dataTestId ? `${dataTestId}-suggestion-${suggestion.id}` : undefined}
          >
            {/* Two lines, both inside the option so they form one accessible name.
                The carrier line is what makes a mis-ranked carrier visible: the
                backend picks one carrier and lists its plans, so without it a
                query for "Cigna Open Access Plus" returns a plausible-looking list
                of Premier Access dental plans with nothing to give it away. */}
            <span className={`block truncate font-medium ${index === activeIndex ? 'text-white' : 'text-slate-900'}`}>
              {suggestion.displayName}
            </span>
            <span className={`block truncate text-sm ${index === activeIndex ? 'text-white/80' : 'text-slate-500'}`}>
              {[suggestion.carrierName, suggestion.state].filter(Boolean).join(' · ')}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
